// ---------------------------------------------------------------------------
// The server-pause overlay.
//
// There are two connection overlays in this game and they must never be
// confusable, because a player who mixes them up will believe they were cheated:
//
//   A server pause freezes EVERYONE's clock, because the server decided and
//   that is fair. One player's disconnect freezes NOBODY's clock, including
//   their own, because freezing one player's clock while the others run is a
//   free-thinking-time exploit.
//
// So this overlay is the only one that may show a stopped clock. It takes the
// whole screen, it desaturates, it greys the board, and every word it uses says
// that nothing is moving. `DisconnectSheet` is its opposite in scope, colour,
// clock behaviour, board treatment, copy, badge and footer. Changing one of
// those without changing the other narrows the gap between them, which is the
// only failure mode that matters here.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import type { Clock } from '@countdown/shared';
import { LiveClock } from '../primitives/index.js';
import { useClock } from '../../hooks/useClock.js';

export interface ServerPauseOverlayProps {
  /**
   * The round clock, so the frozen disc sits at the exact second the round
   * stopped. Null outside a round, which still pauses the room.
   */
  clock: Clock | null;
  /**
   * How many players are sitting the pause out. Null when the caller cannot
   * say, which renders a sentence that does not name a number.
   */
  waitingPlayerCount: number | null;
}

/**
 * The board is drawn by the phase screen underneath, not by this overlay, so
 * the grey is applied by stamping the document and letting one rule reach the
 * tiles. The rule travels with the overlay rather than living in `index.css`
 * because the attribute and its only consumer must not drift apart.
 *
 * Screens mark their tile area with `data-board` for this to reach it.
 */
const BOARD_DIM_CSS = `
[data-server-paused='true'] [data-board] {
  opacity: 0.3;
  filter: saturate(0.2);
  transition: opacity 160ms ease-out;
}
`;

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
];

function spell(count: number): string {
  return NUMBER_WORDS[count] ?? String(count);
}

/** Never claims a count it was not given. */
function waitingLine(count: number | null): string {
  if (count === null || count <= 0) return 'everyone in the room is waiting';
  if (count === 1) return 'one player waiting';
  return `all ${spell(count)} players waiting`;
}

export function ServerPauseOverlay({ clock, waitingPlayerCount }: ServerPauseOverlayProps) {
  // Frozen on purpose, and the only place in the app that is: the pause is the
  // server's, so the wedge holds where it was and the pause glyph sits in the
  // middle of it.
  const reading = useClock(clock, { frozen: true });

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.serverPaused = 'true';
    return () => {
      delete root.dataset.serverPaused;
    };
  }, []);

  return (
    <div
      data-overlay="server-pause"
      role="status"
      aria-live="polite"
      className="fade-in fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 p-7"
      style={{ background: 'var(--c-scrim)' }}
    >
      <style>{BOARD_DIM_CSS}</style>

      <LiveClock
        clock={clock}
        frozen
        size={128}
        variant="frozen"
        label={`Paused with ${reading.seconds} seconds still to run`}
      />

      <div className="flex flex-col items-center gap-3">
        <p className="label-mono rounded-chip border border-line-strong px-3 py-2 text-ink-muted">
          everything stopped
        </p>
        <h2 className="text-center text-[28px]/[1.2] font-bold text-pretty">Server updating</h2>
        <p className="max-w-[270px] text-center text-base/[1.5] text-ink-muted text-pretty">
          The round resumes at the same second it stopped.
        </p>
      </div>

      <div className="flex items-center gap-2.5 rounded-[9px] bg-surface px-3.5 py-3">
        <span aria-hidden="true" className="size-2 rounded-full bg-ink-muted" />
        <p className="font-mono text-xs/[1] font-medium text-ink-muted">
          {waitingLine(waitingPlayerCount)}
        </p>
      </div>
    </div>
  );
}
